import SectionHeading from "@/components/base/SectionHeading";
import {
  calculatorRules,
  electricityTable,
  keyNumbers,
  modelModules,
  pilotNumbers,
} from "@/mocks/home";

const CALCULATOR_URL =
  "https://storage.helloreaddy.io/project_files/796c258f-5aa6-424f-aa3d-52a70118bf3b/1b96a339-5fb7-45cc-b417-50989aefa17c_green-iron-calculator.html";

export default function Numbers() {
  return (
    <section id="numbers" className="bg-background-100 py-20 md:py-28">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-10">
        <SectionHeading
          eyebrow="The plant in numbers"
          title="Every figure follows three simple rules"
          description="Applied to one 50-tonne-a-day module running on renewable power, three rules of chemistry and physics set every number in this project."
        />

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
          {calculatorRules.map((rule, index) => (
            <article
              key={rule.tag}
              className="reveal rounded-lg border border-background-200 bg-background-50 p-6"
              style={{ transitionDelay: `${index * 70}ms` }}
            >
              <span className="font-heading text-3xl font-light text-background-300">{rule.tag}</span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{rule.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{rule.text}</p>
            </article>
          ))}
        </div>

        <div className="reveal mt-16 overflow-hidden rounded-xl border border-background-200 bg-background-50">
          <div className="flex flex-col gap-4 border-b border-background-200 p-6 md:flex-row md:items-end md:justify-between md:p-8">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium uppercase tracking-[0.16em] text-primary-700">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
                Live calculator
              </span>
              <h3 className="mt-4 font-heading text-2xl font-semibold text-foreground-950">
                Green iron calculator
              </h3>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
                Change the module size, hydrogen intensity or power price and watch pellets, hydrogen,
                electricity and oxygen update in real time.
              </p>
            </div>
            <span className="hidden items-center gap-2 rounded-md bg-secondary-100 px-4 py-2 text-xs font-medium text-secondary-900 md:inline-flex">
              <i className="ri-cursor-line" />
              Interactive — drag the sliders
            </span>
          </div>
          <iframe
            src={CALCULATOR_URL}
            title="Green iron plant calculator"
            className="h-[820px] w-full border-0 bg-background-50 md:h-[900px]"
            allow="fullscreen"
            aria-label="Interactive green iron plant calculator"
          />
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="reveal rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">
              Every year, per module
            </h3>
            <p className="mt-1 text-sm text-foreground-500">330 operating days</p>
            <ul className="mt-6 space-y-3">
              {keyNumbers.map((item) => (
                <li
                  key={item.label}
                  className="flex items-center justify-between border-b border-background-200 pb-3 text-sm last:border-0 last:pb-0"
                >
                  <span className="text-foreground-600">{item.label}</span>
                  <span className="font-heading text-base font-semibold text-foreground-950">{item.value}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="reveal rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">
              Electricity per tonne of HBI
            </h3>
            <p className="mt-1 text-sm text-foreground-500">Basis: 67% Fe pellets · 57 kg H₂ · 52 kWh/kg H₂</p>
            <ul className="mt-6 space-y-3">
              {electricityTable.map((row) => (
                <li
                  key={row.stage}
                  className="flex items-center justify-between gap-4 border-b border-background-200 pb-3 text-sm last:border-0 last:pb-0"
                >
                  <span className="text-foreground-600">{row.stage}</span>
                  <span className="whitespace-nowrap font-heading text-base font-semibold text-foreground-950">
                    {row.value} kWh
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex items-start gap-3 rounded-md bg-secondary-50 p-4">
              <i className="ri-information-line mt-0.5 text-secondary-700" />
              <p className="text-xs leading-relaxed text-foreground-700">
                Phase 2 melting, casting and rolling bring the total to about 4,900 kWh per tonne of finished steel.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 rounded-xl border border-background-200 bg-background-50 p-6 md:grid-cols-2 md:p-8 lg:grid-cols-4">
          {pilotNumbers.map((item, index) => (
            <div
              key={item.label}
              className="reveal flex items-start gap-4"
              style={{ transitionDelay: `${index * 60}ms` }}
            >
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className={`${item.icon} text-xl`} />
              </span>
              <span>
                <span className="block font-heading text-lg font-semibold text-foreground-950">{item.value}</span>
                <span className="mt-0.5 block text-xs leading-relaxed text-foreground-500">{item.label}</span>
              </span>
            </div>
          ))}
        </div>

        <div className="reveal mt-12 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="flex flex-col gap-2 border-b border-background-200 p-6 md:flex-row md:items-center md:justify-between md:p-8">
            <div>
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                A module for any steel plant
              </h3>
              <p className="mt-1 text-sm text-foreground-500">
                One electrolyser block, one hydrogen shaft furnace, one HBI press — copy it to scale.
              </p>
            </div>
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary-100 px-3.5 py-1.5 text-xs font-medium text-primary-700">
              <i className="ri-stack-line" />
              Modular by design
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/60 text-xs uppercase tracking-wider text-foreground-500">
                  <th className="px-6 py-3 font-medium">Modules</th>
                  <th className="px-6 py-3 font-medium">Green iron</th>
                  <th className="px-6 py-3 font-medium">Per year</th>
                  <th className="px-6 py-3 font-medium">Electrolysers</th>
                  <th className="px-6 py-3 font-medium">Solar + wind</th>
                  <th className="px-6 py-3 font-medium">Hydrogen</th>
                </tr>
              </thead>
              <tbody>
                {modelModules.map((row, index) => (
                  <tr
                    key={row.modules}
                    className={`border-b border-background-200 last:border-0 ${
                      index === 0 ? "bg-primary-50/60" : ""
                    }`}
                  >
                    <td className="px-6 py-4 font-heading text-base font-semibold text-foreground-950">
                      {row.modules}
                    </td>
                    <td className="px-6 py-4 text-foreground-700">{row.iron}</td>
                    <td className="px-6 py-4 text-foreground-700">{row.year}</td>
                    <td className="px-6 py-4 text-foreground-700">{row.electrolyser}</td>
                    <td className="px-6 py-4 text-foreground-700">{row.power}</td>
                    <td className="px-6 py-4 text-foreground-700">{row.hydrogen}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}